/**
 * O comprimento mínimo da senha, do lado da tela.
 *
 * ## Por que há um número aqui, se a política é do servidor
 *
 * O servidor é a autoridade: ele confere o comprimento, as palavras comuns e —
 * o que a tela não pode fazer — as palavras do CONTEXTO de quem está
 * escolhendo (o próprio nome, o e-mail, o nome da empresa). A tela não tem o
 * nome da empresa de quem aceita um convite antes de aceitá-lo, e nem deveria.
 *
 * Então o que a tela confere é só o óbvio, para não gastar uma ida ao servidor
 * com uma senha de quatro letras. Toda recusa que importa vem do servidor, com
 * a frase dele.
 *
 * ## E por que o número está num lugar só
 *
 * Ele estava escrito à mão em **quatro** arquivos de tela, com quatro frases
 * diferentes, e mais duas vezes no servidor. Seis lugares para um número: é
 * assim que ele muda em cinco e fica velho no sexto, e o que ficou para trás
 * passa a ser a porta.
 *
 * O `H29` do doctor compara este número com o do servidor
 * (`backend/src/sessao/politica-de-senha.ts`) e reprova se eles divergirem — a
 * mesma ideia do `H23`, que compara as 27 UFs nas três camadas. Duas camadas
 * discordando sobre a mesma regra é pior do que uma só, porque a mais frouxa
 * é a que vale.
 */
/* OITO, por decisao do dono (27/10/2026). O porque e a consequencia estao
   escritos em backend/src/sessao/politica-de-senha.ts, que e quem MANDA:
   a tela so adianta a recusa para a pessoa nao enviar e levar 422. */
export const COMPRIMENTO_MINIMO_DA_SENHA = 8;

/**
 * O teto, pelo mesmo motivo do mínimo estar aqui: ele estava escrito à mão
 * como `maxLength={200}` em cinco campos de três telas. Ele existe para o
 * `argon2` não receber um megabyte, e não para limitar frase — e o servidor o
 * confere de qualquer forma.
 */
export const COMPRIMENTO_MAXIMO_DA_SENHA = 200;

/**
 * A validação ao SAIR do campo, que é o que o manual pede (p.16): o erro
 * aparece abaixo do campo quando a pessoa sai dele, e não num alerta solto no
 * topo depois de enviar.
 *
 * Campo vazio não acusa: quem só passou pelo campo e voltou não errou nada —
 * acusar ali transforma navegar por teclado numa sequência de erros. A
 * obrigatoriedade é conferida no envio, onde ela significa algo.
 */
export function validarSenhaNoCampo(valor: string): string | null {
  if (valor === '') return null;
  return problemaObvioNaSenha(valor);
}

/** A repetição confere igualdade, e também não acusa campo vazio. */
export function validarRepeticao(senha: string) {
  return (repeticao: string): string | null => {
    if (repeticao === '') return null;
    return repeticao === senha ? null : 'As duas senhas não são iguais. Digite a mesma senha nos dois campos.';
  };
}

/**
 * O que a tela confere antes de enviar. `null` quer dizer "pode tentar".
 *
 * NÃO é a política: é o primeiro filtro. A mensagem diz o que fazer, e a dica
 * da frase vem antes da cobrança do número — quem lê "ao menos 12 caracteres"
 * pensa em `Senha12345678`; quem lê "três ou quatro palavras" escreve algo
 * imprevisível e mais fácil de lembrar.
 */
export function problemaObvioNaSenha(senha: string): string | null {
  if (senha.trim().length < COMPRIMENTO_MINIMO_DA_SENHA) {
    return `Use uma frase curta — três ou quatro palavras seguidas, com ao menos ${String(COMPRIMENTO_MINIMO_DA_SENHA)} caracteres. Não é preciso símbolo nem número.`;
  }
  return null;
}

/** A ajuda do campo, igual nas quatro telas que pedem senha. */
export const AJUDA_DA_SENHA =
  `Ao menos ${String(COMPRIMENTO_MINIMO_DA_SENHA)} caracteres. Uma frase curta funciona bem e é mais fácil de lembrar; não é preciso símbolo nem número.`;
